import { Badge } from "@/components/_ui/badge";
import { cn } from "@/utils/utils";

export interface ActivityTimelineEvent {
  id: string;
  /** Short label shown as a badge next to the title (e.g. "Latest"). */
  badgeLabel: string;
  /** Key into `colorMap` for the badge style. */
  category: string;
  /** Primary line — typically the event's status label. */
  title: string;
  /** Secondary line — typically the location. */
  actor?: string;
  /** ISO 8601 timestamp; formatted via the required `formatTimestamp` prop. */
  timestamp: string;
  /** When true, the badge renders and the dot is filled. */
  highlighted?: boolean;
  /** Tailwind classes composed onto the timeline dot. */
  dotClassName?: string;
}

export interface ActivityTimelineProps<C extends string = string> {
  events: ActivityTimelineEvent[];
  colorMap: Record<C, string>;
  formatTimestamp: (iso: string) => string;
}

/**
 * Vertical event timeline. Slim port of the admin component covering exactly
 * what the public tracking scan list needs — sorted newest-first, hairline
 * stem, colored dot per event, optional badge for the latest entry.
 */
export function ActivityTimeline<C extends string>({
  events,
  colorMap,
  formatTimestamp,
}: ActivityTimelineProps<C>) {
  if (events.length === 0) return null;

  const sorted = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <div className="relative pl-6">
      <div className="bg-border absolute top-2 bottom-2 left-[5px] w-px" />
      {sorted.map((event, index) => (
        <div
          key={event.id}
          className={cn(
            "relative",
            index < sorted.length - 1 ? "pb-6" : "pb-0",
          )}
        >
          <div
            className={cn(
              "absolute top-1.5 -left-6 size-2.5 rounded-full border-2",
              event.dotClassName ??
                (event.highlighted
                  ? "border-current bg-current"
                  : "bg-background border-current"),
            )}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium">{event.title}</p>
              {event.highlighted && event.badgeLabel ? (
                <Badge
                  variant="outline"
                  className={cn(
                    "shrink-0 border-transparent text-[10px] font-semibold",
                    colorMap[event.category as C],
                  )}
                >
                  {event.badgeLabel}
                </Badge>
              ) : null}
              <span className="text-muted-foreground ml-auto shrink-0 text-xs whitespace-nowrap">
                {formatTimestamp(event.timestamp)}
              </span>
            </div>
            {event.actor ? (
              <p className="text-muted-foreground text-xs">{event.actor}</p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
